// src/pages/Legal.tsx
import React from "react";
import SEOHead from "../components/SEOHead";
import PageTransition from "../components/PageTransition";
import AnimatedSection from "../components/AnimatedSection";
import GradientHero from '../components/GradientHero';
import { motion } from "framer-motion";
import {
  Building2, Mail, Phone, Globe, FileText, Shield, Link as LinkIcon,
  User, Server, Scale, Copyright, AlertTriangle, BookOpen, Cookie
} from "lucide-react";

const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, duration: 0.5 },
  }),
};

const lastUpdate = "26/09/2025"; // Europe/Paris

const Legal: React.FC = () => {
  // JSON-LD (Organisation + WebSite)
  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "name": "Neoqualis",
        "url": "https://neoqualis.com",
        "email": "contact@neoqualis.com",
        "telephone": "+33 1 86 04 61 82",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "2-4 Rue Vincent Van Gogh",
          "postalCode": "93360",
          "addressLocality": "Neuilly-Plaisance",
          "addressCountry": "FR"
        },
        "contactPoint": [{
          "@type": "ContactPoint",
          "contactType": "Service client",
          "email": "contact@neoqualis.com",
          "telephone": "+33 1 86 04 61 82",
          "areaServed": "FR"
        }]
      },
      {
        "@type": "WebPage",
        "name": "Mentions légales",
        "url": "https://neoqualis.com/legal",
        "description": "Mentions légales du site Neoqualis : éditeur, directeur de la publication, hébergeur, propriété intellectuelle et responsabilités."
      }
    ]
  };

  return (
    <PageTransition>
      <SEOHead
        title="Mentions légales | Neoqualis"
        description="Mentions légales du site Neoqualis : informations éditeur, directeur de la publication, hébergeur Hostinger, droits et responsabilités."
        canonical="https://neoqualis.com/legal"
        jsonLd={jsonLd}
      />

      <div className="pt-16">
        {/* Hero */}
        <GradientHero
          title="Mentions légales"
          subtitle={`Dernière mise à jour : ${lastUpdate}`}
        />

        {/* Intro */}
        <section className="section bg-white">
          <div className="container max-w-4xl">
            <AnimatedSection>
              <p className="text-gray-700">
                Les présentes mentions légales sont établies conformément au droit français et notamment à la
                loi n°2004-575 du 21 juin 2004 pour la confiance dans l’économie numérique.
              </p>
            </AnimatedSection>
          </div>
        </section>

        {/* Éditeur & Publication */}
        <section className="section bg-gray-50">
          <div className="container max-w-6xl">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Éditeur du site */}
              <motion.div
                custom={0}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={cardVariants}
              >
                <div className="card h-full">
                  <div className="flex items-center mb-4">
                    <div className="p-3 rounded-full bg-primary/10">
                      <Building2 className="text-primary w-7 h-7" />
                    </div>
                    <h2 className="text-xl font-semibold ml-4 text-gray-900">
                      1. Éditeur du site
                    </h2>
                  </div>
                  <div className="text-gray-700 space-y-2">
                    <p><strong>Neoqualis</strong></p>
                    <p>2-4 Rue Vincent Van Gogh,<br />93360 Neuilly-Plaisance — France</p>
                    <p className="flex items-center">
                      <Mail className="w-4 h-4 mr-2" /> contact@neoqualis.com
                    </p>
                    <p className="flex items-center">
                      <Phone className="w-4 h-4 mr-2" /> +33 1 86 04 61 82
                    </p>
                    {/* Remplir si connu — SIREN / SIRET / RCS / Capital */}
                    <p className="text-sm text-gray-500">
                      907 885 784 R.C.S. Bobigny au capital de : 40 000,00 €
                    </p>
                  </div>
                </div>
              </motion.div>

              {/* Directeur de la publication */}
              <motion.div
                custom={1}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={cardVariants}
              >
                <div className="card h-full">
                  <div className="flex items-center mb-4">
                    <div className="p-3 rounded-full bg-secondary/10">
                      <User className="text-secondary w-7 h-7" />
                    </div>
                    <h2 className="text-xl font-semibold ml-4 text-gray-900">
                      2. Directeur de la publication
                    </h2>
                  </div>
                  <div className="text-gray-700 space-y-2">
                    <p><strong>M. Aghilas DAHMANI</strong></p>
                    <p className="text-sm text-gray-500">
                      Président
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Hébergeur */}
        <section className="section bg-white">
          <div className="container max-w-6xl">
            <motion.div
              custom={0}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={cardVariants}
            >
              <div className="card h-full">
                <div className="flex items-center mb-4">
                  <div className="p-3 rounded-full bg-primary/10">
                    <Server className="text-primary w-7 h-7" />
                  </div>
                  <h2 className="text-xl font-semibold ml-4 text-gray-900">
                    3. Hébergeur du site
                  </h2>
                </div>
                <div className="text-gray-700 space-y-2">
                  <p><strong>Hostinger International Ltd.</strong></p>
                  <p className="flex items-center">
                    <Globe className="w-4 h-4 mr-2" /> <span>https://www.hostinger.com</span>
                  </p>
                  <p className="text-sm text-gray-600">
                    L’infrastructure d’hébergement est localisée au sein de l’Union européenne.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Propriété intellectuelle */}
        <section className="section bg-gray-50">
          <div className="container max-w-6xl">
            <motion.div
              custom={0}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={cardVariants}
            >
              <div className="card h-full">
                <div className="flex items-center mb-4">
                  <div className="p-3 rounded-full bg-secondary/10">
                    <Copyright className="text-secondary w-7 h-7" />
                  </div>
                  <h2 className="text-xl font-semibold ml-4 text-gray-900">
                    4. Propriété intellectuelle
                  </h2>
                </div>
                <div className="text-gray-700 space-y-3">
                  <p>
                    Le Site et l’ensemble de ses contenus (textes, visuels, logos, maquettes, icônes, codes et logiciels)
                    sont protégés par le droit d’auteur et/ou le droit des marques. Toute reproduction, représentation,
                    adaptation, traduction, diffusion ou rediffusion, totale ou partielle, sans autorisation écrite
                    préalable de Neoqualis est interdite.
                  </p>
                  <p className="text-sm text-gray-600">
                    Les marques et logos de tiers mentionnés sur le Site demeurent la propriété de leurs titulaires respectifs.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Données personnelles & Cookies */}
        <section className="section bg-white">
          <div className="container max-w-6xl">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Données personnelles */}
              <motion.div
                custom={0}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={cardVariants}
              >
                <div className="card h-full">
                  <div className="flex items-center mb-4">
                    <div className="p-3 rounded-full bg-primary/10">
                      <Shield className="text-primary w-7 h-7" />
                    </div>
                    <h2 className="text-xl font-semibold ml-4 text-gray-900">
                      5. Données personnelles (RGPD)
                    </h2>
                  </div>
                  <p className="text-gray-700">
                    Pour toute information sur la collecte et le traitement de vos données, ainsi que l’exercice de vos droits,
                    veuillez consulter notre{" "}
                    <a href="/privacy" className="text-primary underline">
                      Politique de confidentialité
                    </a>.
                  </p>
                </div>
              </motion.div>

              {/* Cookies */}
              <motion.div
                custom={1}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={cardVariants}
              >
                <div className="card h-full">
                  <div className="flex items-center mb-4">
                    <div className="p-3 rounded-full bg-secondary/10">
                      <Cookie className="text-secondary w-7 h-7" />
                    </div>
                    <h2 className="text-xl font-semibold ml-4 text-gray-900">
                      6. Cookies & traceurs
                    </h2>
                  </div>
                  <p className="text-gray-700">
                    Par défaut, le Site ne dépose aucun cookie non essentiel. En cas d’activation de traceurs soumis au
                    consentement, un bandeau d’information vous permettra d’accepter ou de refuser. Détails dans la{" "}
                    <a href="/privacy" className="text-primary underline">
                      Politique de confidentialité
                    </a>.
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Liens, Responsabilité, Droit applicable */}
        <section className="section bg-gray-50">
          <div className="container max-w-6xl">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {/* Liens */}
              <motion.div
                custom={0}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={cardVariants}
              >
                <div className="card h-full">
                  <div className="flex items-center mb-4">
                    <div className="p-3 rounded-full bg-primary/10">
                      <LinkIcon className="text-primary w-7 h-7" />
                    </div>
                    <h2 className="text-xl font-semibold ml-4 text-gray-900">
                      7. Liens & sites tiers
                    </h2>
                  </div>
                  <p className="text-gray-700 text-sm">
                    Le Site peut contenir des liens vers des sites tiers. Neoqualis n’exerce aucun contrôle sur ces
                    ressources et ne saurait être tenue responsable de leur contenu, fonctionnement ou disponibilité.
                  </p>
                </div>
              </motion.div>

              {/* Responsabilité */}
              <motion.div
                custom={1}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={cardVariants}
              >
                <div className="card h-full">
                  <div className="flex items-center mb-4">
                    <div className="p-3 rounded-full bg-secondary/10">
                      <AlertTriangle className="text-secondary w-7 h-7" />
                    </div>
                    <h2 className="text-xl font-semibold ml-4 text-gray-900">
                      8. Responsabilité
                    </h2>
                  </div>
                  <p className="text-gray-700 text-sm">
                    Les informations publiées le sont à titre informatif. Malgré notre vigilance, des erreurs ou omissions
                    peuvent subsister. Neoqualis ne pourra être tenue responsable des dommages indirects, pertes d’exploitation
                    ou de données liés à l’usage du Site.
                  </p>
                </div>
              </motion.div>

              {/* Droit applicable */}
              <motion.div
                custom={2}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={cardVariants}
              >
                <div className="card h-full">
                  <div className="flex items-center mb-4">
                    <div className="p-3 rounded-full bg-primary/10">
                      <Scale className="text-primary w-7 h-7" />
                    </div>
                    <h2 className="text-xl font-semibold ml-4 text-gray-900">
                      9. Droit applicable
                    </h2>
                  </div>
                  <p className="text-gray-700 text-sm">
                    Les présentes mentions légales sont régies par le droit français. En cas de litige, et sauf dispositions
                    légales d’ordre public contraires, les tribunaux de <strong>Paris</strong> seront compétents.
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Contacts utiles */}
        <section className="section bg-white">
          <div className="container max-w-6xl">
            <motion.div
              custom={0}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={cardVariants}
            >
              <div className="card h-full">
                <div className="flex items-center mb-4">
                  <div className="p-3 rounded-full bg-secondary/10">
                    <BookOpen className="text-secondary w-7 h-7" />
                  </div>
                  <h2 className="text-xl font-semibold ml-4 text-gray-900">
                    10. Contacts utiles
                  </h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-gray-700">
                  <div>
                    <p className="font-semibold mb-1">Éditeur</p>
                    <p>Neoqualis</p>
                    <p>2-4 Rue Vincent Van Gogh, 93360 Neuilly-Plaisance — France</p>
                    <p className="flex items-center mt-1"><Mail className="w-4 h-4 mr-2" /> contact@neoqualis.com</p>
                    <p className="flex items-center"><Phone className="w-4 h-4 mr-2" /> +33 1 86 04 61 82</p>
                  </div>
                  <div>
                    <p className="font-semibold mb-1">Hébergeur</p>
                    <p>Hostinger International Ltd.</p>
                    <p className="flex items-center mt-1"><Globe className="w-4 h-4 mr-2" /> https://www.hostinger.</p>
                  </div>
                </div>
                <div className="text-sm text-gray-600 mt-6">
                  <p>
                    Pour toute question relative aux données personnelles, consultez la{" "}
                    <a href="/privacy" className="text-primary underline">Politique de confidentialité</a>.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Liens internes bas de page */}
        <section className="section bg-gray-50">
          <div className="container">
            <div className="card max-w-4xl mx-auto text-center">
              <p className="text-gray-700">
                Documents associés :{" "}
                <a href="/privacy" className="text-primary underline">Politique de confidentialité</a> •{" "}
                <a href="/terms" className="text-primary underline">CGU</a>
              </p>
            </div>
          </div>
        </section>
      </div>
    </PageTransition>
  );
};

export default Legal;
